/* eslint-disable max-statements */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { message } from 'antd';
import axios, { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import lodash from 'lodash';

import CONFIG from '@config/index';
import store from '@core/store/redux';
import { delay, sendLogOut } from '@helper/functions';
import locale, { Locale } from '@locale/index';
import { RootState } from '@modules';
import { removeProfile, setToken } from '@modules/authentication/profileStore';

export type TShowError = boolean | string | string[];

export interface IParamsHTTP<T> {
  method?: 'get' | 'post' | 'delete' | 'put';
  path: string;
  payload?: any;
  params?: any;
  config?: {
    responseType?: 'arraybuffer';
    isFormData?: boolean;
    signal?: AbortSignal;
    sessionId?: string;
  };
  showSuccess?: boolean;
  showError?: TShowError;
  convert?: (res: any) => T;
}

export class HTTPRepository {
  private service: AxiosInstance;

  private token?: any;

  private refreshToken?: any;

  private language: keyof Locale = 'en';

  private refreshUrl: string = '/api/Users/RefreshToken';

  private refreshTokenController?: AbortController;
  // private isRefresh:

  constructor(baseURL?: string, refreshUrl?: string) {
    this.service = axios.create({
      baseURL: baseURL || CONFIG.API_BASE_URL,
      withCredentials: false,
    });
    if (refreshUrl != null) {
      this.refreshUrl = refreshUrl;
    }
    const state: RootState = store.getState();
    this.token = state?.profile?.token;
    this.language = state.settingStore.language;

    store.subscribe(() => {
      const newState: RootState = store.getState();
      this.token = newState.profile.token;
      this.language = newState.settingStore.language;
    });
  }

  private handleSuccess(response: any, convert: any, showSuccess: any) {
    if (showSuccess) {
      message.success(locale[this.language][response?.data?.message] || response?.data?.message);
    }
    if (convert !== undefined) {
      return Promise.resolve(convert(response.data?.data));
    }
    return Promise.resolve(response);
  }

  refreshTokenFunction = (): Promise<{ token: string; refreshToken: string }> => {
    this.refreshTokenController = new AbortController();
    return this.execute({
      path: this.refreshUrl,
      method: 'post',
      payload: { refreshToken: this.refreshToken },
      config: { signal: this.refreshTokenController.signal },
      showSuccess: false,
      convert: rs => {
        return {
          token: rs.accessToken,
          refreshToken: rs.refreshToken,
        };
      },
    });
  };

  private async handleError(error: any, showError: any, arr: any) {
    const status = error.response?.status;
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    switch (status) {
      case 400: {
        if (showError) {
          const isString: boolean =
            typeof showError === 'string' && showError === error.response?.data?.message;
          const isArrayString: boolean =
            Array.isArray(showError) &&
            showError?.some(
              (it: any) => typeof it === 'string' && it === error.response?.data?.message,
            );

          if (!(isString || isArrayString)) {
            message.error(
              locale[this.language][error.response?.data?.message] || error.response?.data?.message,
            );
          }
        }
        break;
      }
      case 401: {
        if (this.token == null || this.refreshToken == null) {
          store.dispatch(removeProfile());
          Cookies.remove('accessTokenSSO');
          Cookies.remove('refreshTokenSSO');
          sendLogOut();
          window.location.href = CONFIG.LOGIN_PAGE;
          break;
        }
        if (this.refreshTokenController != null) {
          return delay(1000).then(() => this.execute(arr));
        }
        try {
          const rs = await this.refreshTokenFunction();
          this.refreshTokenController = undefined;
          store.dispatch(
            setToken({
              token: rs.token,
              remember: true,
              refreshToken: undefined,
            }),
          );
          // eslint-disable-next-line @typescript-eslint/return-await
          return this.execute(arr);
        } catch (e) {
          this.refreshTokenController = undefined;
          store.dispatch(removeProfile());
          Cookies.remove('accessTokenSSO');
          Cookies.remove('refreshTokenSSO');
          sendLogOut();
          window.location.href = CONFIG.LOGIN_PAGE;
          return Promise.reject(e);
        }
      }
      case 500: {
        message.error(
          locale[this.language][error.response?.data?.message] || error.response?.data?.message,
        );
        break;
      }
      case undefined: {
        if (showError) {
          message.error(locale[this.language]['server.networkError']);
        }
        break;
      }
      default: {
        if (showError) {
          message.error('HTTP CODE ' + status);
        }
        break;
      }
    }
    return Promise.reject(error);
  }

  private preparePrivateHeaderConfig() {
    if (lodash.isEmpty(this.token)) {
      return {};
    }
    return {
      Authorization: `Bearer ${this.token}`,
    };
  }

  private getDefaultConfig({ isFormData, sessionId }: any = {}) {
    const config = {
      headers: {},
    };

    const privateHeaderConfig = this.preparePrivateHeaderConfig();
    Object.assign(config.headers, privateHeaderConfig);

    if (isFormData) {
      Object.assign(config.headers, {
        'Content-Type': 'multipart/form-data',
      });
    }
    if (sessionId) {
      Object.assign(config.headers, {
        sessionId,
      });
    }
    return config;
  }

  execute<T>({
    method = 'get',
    path = '',
    payload,
    config = { responseType: undefined },
    params,
    showSuccess = true,
    showError = true,
    convert = res => res,
  }: IParamsHTTP<T>): Promise<T> {
    let args: Array<any>;
    const { isFormData = false, responseType, signal, sessionId } = config;

    switch (method) {
      case 'get': {
        if (params) {
          const paramsData: URLSearchParams = Object.keys(params).reduce((url, key) => {
            if (Array.isArray(params[key])) {
              params[key].forEach((element: string | null) => {
                if (element != null) {
                  url.append(key, element);
                }
              });
            } else if (params[key] != null) {
              url.append(key, params[key]);
            }
            return url;
          }, new URLSearchParams());
          args = [
            path,
            {
              ...this.getDefaultConfig({ sessionId }),
              responseType,
              signal,
              params: paramsData,
            },
          ];
        } else {
          args = [path, { ...this.getDefaultConfig({ sessionId }), responseType }];
        }
        break;
      }
      case 'delete': {
        args = [
          path,
          {
            data: payload,
            ...this.getDefaultConfig({ sessionId }),
            signal,
            params: params ? params : null,
          },
        ];
        break;
      }
      case 'post':
      case 'put': {
        function processData(data: any, formData: any, parentKey?: string) {
          Object.keys(data).forEach(key => {
            if (data.hasOwnProperty(key)) {
              const value = data[key];
              const fieldName = parentKey ? `${parentKey}.${key}` : key;
              if (value != null) {
                if (Array.isArray(value)) {
                  value.forEach((item, index) => {
                    if (item != null) {
                      if (typeof item === 'string' || item instanceof File) {
                        formData.append(`${fieldName}`, item);
                      } else if (typeof item === 'object') {
                        processData(item, formData, `${fieldName}[${index}]`);
                      }
                    }
                  });
                } else if (typeof value === 'string' || value instanceof File) {
                  formData.append(`${fieldName}`, value);
                } else if (typeof value === 'object') {
                  processData(value, formData, fieldName);
                } else {
                  formData.append(fieldName, value);
                }
              }
            }
          });
        }
        const data = isFormData ? new FormData() : payload;
        if (isFormData) {
          processData(payload, data);
        }
        args = [
          path,
          data,
          { ...this.getDefaultConfig({ isFormData, sessionId }), responseType, signal },
        ];
        break;
      }
      default:
        break;
    }

    //@ts-ignore
    return this.service[method](...args)
      .then(response => {
        return this.handleSuccess(response, convert, showSuccess);
      })
      .catch(error =>
        this.handleError(error, showError, {
          method,
          path,
          payload,
          config,
          params,
          showSuccess,
          showError,
          convert,
        }),
      );
  }
}

const httpRepository = new HTTPRepository();

export default httpRepository;
